// useConsultantMemory — powers the consultant header ⋯ menu (Moat Plan A1/A2
// user controls). Reads whether personalization is on + what the consultant
// currently remembers (the rolling A2 summary), and exposes a toggle + a
// one-tap "clear what it remembers".
//
// When personalization is OFF the consultant gives generic answers (the edge
// fn skips the whole USER PROFILE block) and the cron summarizer stops writing
// memory for this user. Clearing hard-deletes the stored memory (it can rebuild
// from conversation history if re-enabled).

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";

interface ConsultantMemoryState {
  enabled: boolean;
  summary: string | null;
  topics: string[];
}

export function useConsultantMemory() {
  const { session } = useUserProfile();
  const userId = session?.user.id ?? null;
  const qc = useQueryClient();
  const queryKey = ["consultant-memory", userId];

  const query = useQuery<ConsultantMemoryState>({
    queryKey,
    queryFn: async () => {
      if (!userId) return { enabled: true, summary: null, topics: [] };
      const [profRes, memRes] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from("profiles")
          .select("consultant_personalization_enabled")
          .eq("user_id", userId)
          .maybeSingle(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from("consultant_user_memory")
          .select("memory_summary, recurring_topics")
          .eq("is_latest", true)
          .maybeSingle(),
      ]);
      const topics = memRes.data?.recurring_topics;
      return {
        enabled: profRes.data?.consultant_personalization_enabled !== false,
        summary: memRes.data?.memory_summary ?? null,
        topics: Array.isArray(topics) ? (topics as string[]) : [],
      };
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  });

  const setEnabledMut = useMutation<void, Error, boolean>({
    mutationFn: async (enabled) => {
      if (!userId) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("profiles")
        .update({ consultant_personalization_enabled: enabled })
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const clearMut = useMutation<void, Error, void>({
    mutationFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc("clear_consultant_memory");
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  return {
    enabled: query.data?.enabled ?? true,
    summary: query.data?.summary ?? null,
    topics: query.data?.topics ?? [],
    loading: query.isLoading,
    setEnabled: (v: boolean) => setEnabledMut.mutate(v),
    clearMemory: () => clearMut.mutate(),
    isSaving: setEnabledMut.isPending || clearMut.isPending,
  };
}
